import React, { memo, useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import VoicemailGreetingSettings from './voicemail-greeting-settings';
import { RootState } from '../../../../../../../../../../store/root-reducer';
import { PhoneNumberSettingCode } from '../../../../../../../../types/phone-numbers';
import {
  updatePhoneNumberSettingsRequest,
  getPhoneNumberDetailsRequest,
} from '../../../../../../../../extra-actions';
import { RequestStatus } from '../../../../../../../../../../shared/enums/request-status';
import { resetUpdatePhoneNumberSettingsRequest } from '../../../../../../../../dialer-slice';
import toaster, { Theme } from '../../../../../../../../../../shared/toaster';
import { getPreSignedUrl } from '../../../../../../../../helpers/dialer.api';

const VoicemailGreetingSettingsContainer: React.FC = () => {
  const dispatch = useDispatch();
  const activeNumberSettings = useSelector(
    (state: RootState) => state.dialer.activeNumberSettings,
  );
  const updatePhoneNumberSettingsRequestStatus = useSelector(
    (state: RootState) =>
      state.dialer.updatePhoneNumberSettingsRequest[
        PhoneNumberSettingCode.VOICEMAIL_ENABLED
      ]?.status || RequestStatus.Ideal,
  );
  const updateVoicemailUrlRequestStatus = useSelector(
    (state: RootState) =>
      state.dialer.updatePhoneNumberSettingsRequest[
        PhoneNumberSettingCode.VOICEMAIL_URL
      ]?.status || RequestStatus.Ideal,
  );
  const isDemoAccount = useSelector(
    (state: RootState) => state.home?.isDemoAccount,
  );

  // Create a map for easy lookup of setting values by code
  const settingsMap: Record<
    PhoneNumberSettingCode,
    string | null
  > = useMemo(() => {
    if (!activeNumberSettings?.settings) {
      return {} as Record<PhoneNumberSettingCode, string | null>;
    }

    return activeNumberSettings.settings.reduce((acc, setting) => {
      acc[setting.code as PhoneNumberSettingCode] = setting.value;
      return acc;
    }, {} as Record<PhoneNumberSettingCode, string | null>);
  }, [activeNumberSettings]);

  const voicemailEnabled =
    settingsMap[PhoneNumberSettingCode.VOICEMAIL_ENABLED];
  const voicemailUrl = settingsMap[PhoneNumberSettingCode.VOICEMAIL_URL];

  const handleToggleVoicemailGreeting = useCallback(
    (enabled: boolean) => {
      if (!activeNumberSettings?.id) {
        return;
      }

      dispatch(
        updatePhoneNumberSettingsRequest({
          phoneNumberIds: [activeNumberSettings.id],
          settings: [
            {
              code: PhoneNumberSettingCode.VOICEMAIL_ENABLED,
              value: enabled ? 'true' : 'false',
            },
          ],
        }),
      );
    },
    [dispatch, activeNumberSettings?.id],
  );

  // Refresh phone number details on successful save
  useEffect(() => {
    if (
      updatePhoneNumberSettingsRequestStatus === RequestStatus.Succeeded &&
      activeNumberSettings?.id
    ) {
      dispatch(getPhoneNumberDetailsRequest(activeNumberSettings.id));
      dispatch(
        resetUpdatePhoneNumberSettingsRequest(
          PhoneNumberSettingCode.VOICEMAIL_ENABLED,
        ),
      );
      toaster.success('Number Setting Updated Successfully', {
        theme: Theme.New,
      });
    }
  }, [
    updatePhoneNumberSettingsRequestStatus,
    activeNumberSettings?.id,
    dispatch,
  ]);

  // Refresh phone number details on successful voicemail URL update
  useEffect(() => {
    if (
      updateVoicemailUrlRequestStatus === RequestStatus.Succeeded &&
      activeNumberSettings?.id
    ) {
      dispatch(getPhoneNumberDetailsRequest(activeNumberSettings.id));
      dispatch(
        resetUpdatePhoneNumberSettingsRequest(
          PhoneNumberSettingCode.VOICEMAIL_URL,
        ),
      );
      toaster.success('Voicemail Greeting Updated Successfully', {
        theme: Theme.New,
      });
    }
  }, [updateVoicemailUrlRequestStatus, activeNumberSettings?.id, dispatch]);

  const handleSaveVoicemail = useCallback(
    async (
      audioBlob: Blob,
      fileName: string,
      fileSize: number,
      duration: number,
    ) => {
      if (!activeNumberSettings?.id) {
        toaster.error('Phone number not found', { theme: Theme.New });
        return;
      }

      try {
        // Step 1: Get pre-signed URL
        const fileExtension = fileName
          .substring(fileName.lastIndexOf('.'))
          .toLowerCase()
          .replace('.', '');
        // Determine MIME type based on file extension (API expects MIME type, not extension)
        const mimeType = fileExtension === 'wav' ? 'audio/wav' : 'audio/mpeg';

        const preSignedUrlResponse = await getPreSignedUrl({
          fileName,
          documentType: 'voicemail-greeting',
          fileType: mimeType, // API expects MIME type, not file extension
          fileSize,
          metadata: { duration },
        });

        // Handle response structure: { payload: { success: true, payload: { url, fileKey, ... } } }
        const outerPayload = preSignedUrlResponse?.payload as any;
        const responseData = outerPayload?.payload;

        if (!responseData?.url || !responseData?.fileKey) {
          throw new Error('Failed to get pre-signed URL');
        }

        const { url: preSignedUrl, fileKey } = responseData;

        // Step 2: Upload file to pre-signed URL
        const uploadResponse = await fetch(preSignedUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': mimeType,
            'Content-Length': fileSize.toString(),
          },
          body: audioBlob,
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload file');
        }

        // Step 3: Update phone number settings with fileKey
        dispatch(
          updatePhoneNumberSettingsRequest({
            phoneNumberIds: [activeNumberSettings.id],
            settings: [
              {
                code: PhoneNumberSettingCode.VOICEMAIL_URL,
                value: fileKey,
              },
            ],
          }),
        );
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to save voicemail';
        toaster.error(errorMessage, { theme: Theme.New });
        throw error;
      }
    },
    [dispatch, activeNumberSettings?.id],
  );

  return (
    <VoicemailGreetingSettings
      isVoicemailGreetingEnabled={voicemailEnabled === 'true'}
      onToggle={handleToggleVoicemailGreeting}
      isLoading={
        updatePhoneNumberSettingsRequestStatus === RequestStatus.Pending ||
        updateVoicemailUrlRequestStatus === RequestStatus.Pending
      }
      audioSrc={voicemailUrl || undefined}
      onSaveVoicemail={handleSaveVoicemail}
      isDemoAccount={isDemoAccount}
    />
  );
};

export default memo(VoicemailGreetingSettingsContainer);
